/**
 * ChatHeader — 에이전트 선택 + 세션 제목 + 대화 액션
 *
 * 구성:
 * - 좌측: AgentSelector (컴팩트)
 * - 중앙: 세션 제목 (클릭 시 rename)
 * - 우측: Stop (스트리밍 중) + "+ 새 대화" + 히스토리 토글
 */

import { useState, useEffect, useRef, type KeyboardEvent } from "react";
import { Plus, Square } from "lucide-react";
import { LayoutHeader, PanelToggleButton } from "@/shared/components";
import { cn } from "@/shared/lib/utils";
import { AgentSelector } from "./agent-selector";
import { useAgentSession, useUpdateSessionTitle } from "@/agent/data/sessions";

export interface ChatHeaderProps {
  agentId: string;
  onAgentChange: (agentId: string) => void;
  sessionId?: string | null;
  isStreaming?: boolean;
  onStop?: () => void;
  onNewChat?: () => void;
  onHistoryToggle?: () => void;
  historyOpen?: boolean;
  className?: string;
}

export function ChatHeader({
  agentId,
  onAgentChange,
  sessionId,
  isStreaming = false,
  onStop,
  onNewChat,
  onHistoryToggle,
  historyOpen = false,
  className,
}: ChatHeaderProps) {
  const { data: session } = useAgentSession(sessionId ?? null);
  const updateTitle = useUpdateSessionTitle();

  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  // 편집 모드 진입 시 focus
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  function startRename() {
    if (!session) return;
    setDraftTitle(session.title);
    setEditing(true);
  }

  function commitRename() {
    if (!sessionId || !session) return;
    const trimmed = draftTitle.trim();
    if (trimmed.length > 0 && trimmed !== session.title) {
      updateTitle.mutate({ sessionId, title: trimmed });
    }
    setEditing(false);
  }

  function cancelRename() {
    setEditing(false);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      e.preventDefault();
      commitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelRename();
    }
  }

  const title =
    session?.title ?? (sessionId ? "대화 불러오는 중..." : "새 대화");

  return (
    <LayoutHeader className={className}>
      {/* History sidebar toggle */}
      {onHistoryToggle && (
        <PanelToggleButton
          isOpen={historyOpen}
          onToggle={onHistoryToggle}
          className="hidden md:flex"
        />
      )}

      {/* Agent selector */}
      <AgentSelector
        selectedAgentId={agentId}
        onSelect={onAgentChange}
        disabled={isStreaming}
        className="shrink-0 max-w-[200px]"
      />

      {/* Session title (center) */}
      <div className="flex min-w-0 flex-1 items-center">
        {editing ? (
          <input
            ref={inputRef}
            type="text"
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            onBlur={commitRename}
            onKeyDown={handleKeyDown}
            className="w-full rounded-md border border-accent/30 bg-surface-base px-2 py-1 text-[13px] font-medium text-text-primary outline-none focus:border-accent/60 focus:ring-2 focus:ring-accent/20"
            maxLength={120}
          />
        ) : (
          <button
            type="button"
            onClick={sessionId && session ? startRename : undefined}
            disabled={!sessionId || !session}
            className={cn(
              "group flex min-w-0 items-center gap-1.5 rounded-md px-2 py-1 text-left transition-colors",
              sessionId && session
                ? "hover:bg-surface-secondary cursor-text"
                : "cursor-default",
            )}
            title={sessionId && session ? "클릭하여 제목 변경" : undefined}
          >
            <span className="truncate text-[13px] font-medium text-text-primary">
              {title}
            </span>
            {session && (
              <span className="shrink-0 text-[11px] text-text-tertiary">
                · {session.messageCount}개
              </span>
            )}
          </button>
        )}
      </div>

      {/* 우측 액션 */}
      <div className="flex shrink-0 items-center gap-1.5">
        {/* Stop (스트리밍 중) */}
        {isStreaming && onStop && (
          <button
            type="button"
            onClick={onStop}
            title="응답 중지"
            className="flex items-center gap-1.5 rounded-lg border border-signal-danger/30 bg-signal-danger/5 px-2.5 py-1.5 text-[12px] font-medium text-signal-danger hover:bg-signal-danger/10 transition-colors"
          >
            <Square className="size-3.5" />
            <span>중지</span>
          </button>
        )}

        {/* 새 대화 — 명시적 텍스트 CTA */}
        <button
          type="button"
          onClick={onNewChat}
          disabled={isStreaming}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-3 py-1.5 text-[12px] font-semibold text-text-inverse transition-colors hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="size-3.5" />
          <span>새 대화</span>
        </button>
      </div>
    </LayoutHeader>
  );
}
